import { Button, Icon } from '@getsynapse/design-system';
import classnames from 'classnames';
import React from 'react';
import { EDIT_BUDGET_TABS } from '../../../../../utils/constants';
import {
  LineItemWithAmounts,
  Option,
  TimeFrame,
} from '../../../../../utils/customTypes';
import { ParentOfLineItems } from '../../../../../utils/types/budget';
import {
  calculateTotalAnnualValue,
  mapRowColorWithLayer,
  numberFormat,
} from '../../../helpers/budgetHelper';
import useBudgetLineItemCalculations from '../../../hooks/useBudgetLineItemCalculations';
import {
  calculateLineItemActualCell,
  calculateLineItemAllocationCell,
  calculateParentActualCell,
  calculateParentAllocationCell,
  calculateTotalAnnualActualValue,
  stickyCellsStyles,
} from './reportHelpers';
import { ActualTimes } from 'utils/constants/budget';
import programIcon from '../../../../../assets/icons/program.svg';
import projectIcon from '../../../../../assets/icons/projects.svg';
import CurrencyField from '../../../../../Atoms/CurrencyField/CurrencyField';

export function generateMultipleReportRows(
  parentList: Array<ParentOfLineItems>,
  layer: number,
  expandedRows: Record<string, boolean>,
  toggleExpandedRow: (rowId: string) => void,
  focusedRows: Record<string, boolean>,
  toggleFocusedRow: (params: {
    rowId: string;
    isFocused?: boolean;
    isOnlyChecked?: boolean;
  }) => void,
  checkedRows: Record<string, boolean>,
  onEditLineItem: (amount: number, lineItemId: string) => void,
  programOptions: Option[],
  projectOptions: Option[],
  vendorOptions: Option[],
  teamOptions: Option[],
  viewType: string,
  fiscalMonthStart: number,
  monthsForYearToDate: number[],
  selectedTime: Option,
  sumOfAmounts: number,
  sumOfActuals: number,
  timeframe: TimeFrame,
  focusedCellId: string,
  setFocusedCellId: (cellId: string) => void,
  isProject?: boolean,
  hideDelete?: boolean,
  handleAddNote?: (item: LineItemWithAmounts) => void,
  handleAddAttachment?: (itemId: string) => void,
  handleDeleteLineItem?: (item: LineItemWithAmounts) => void
): Array<any> {
  let rows = parentList.map((parentInfo) => {
    const isExpanded = expandedRows[parentInfo.id] || isProject;
    const fiscalAmount = calculateTotalAnnualValue(parentInfo);
    const fiscalActual = calculateTotalAnnualActualValue(parentInfo);
    const cellAmount = calculateParentAllocationCell(
      parentInfo,
      timeframe,
      fiscalMonthStart,
      selectedTime,
      monthsForYearToDate
    );

    const cellActual = calculateParentActualCell(
      parentInfo,
      timeframe,
      fiscalMonthStart,
      selectedTime,
      monthsForYearToDate
    );

    return [
      {
        id: `${parentInfo.id}-report-details`,
        className: `group border ${mapRowColorWithLayer(layer)} h-12`,
        cells: [
          {
            content: (
              <div
                className='flex flex-row gap-x-1 cursor-pointer min-w-200px p-2'
                style={{ marginLeft: `${layer * 10}px` }}
                onClick={() => toggleExpandedRow(parentInfo.id)}
              >
                {isExpanded && !isProject && (
                  <Icon name='chevron-down-outline' className='text-xl' />
                )}
                {!isProject && !isExpanded && (
                  <Icon name='chevron-forward-outline' className='text-xl' />
                )}
                {!isProject &&
                  parentInfo.type === 'programId' &&
                  parentInfo.id !== 'none' && (
                    <Icon src={programIcon} className='text-xl' />
                  )}
                {!isProject &&
                  parentInfo.type === 'projectId' &&
                  parentInfo.id !== 'none' && (
                    <Icon src={projectIcon} className='text-xl' />
                  )}
                <div className='table-text-truncate flex items-center overflow-hidden'>
                  {parentInfo.title}
                </div>
              </div>
            ),
            className: stickyCellsStyles(
              classnames('min-w-200px left-0 py-0 px-1 h-12'),
              layer
            ),
          },
          // q1
          {
            content: '$' + numberFormat(cellAmount),
            className: 'min-w-157px h-12 text-right py-0',
          },
          {
            content: '$' + numberFormat(cellActual),
            className: 'min-w-157px h-12 text-right py-0',
          },
          {
            content: (
              <div
                className={`h-12 text-right flex items-center justify-end ${
                  cellAmount - cellActual < 0 ? 'text-error-darker' : ''
                }`}
              >
                {'$' + numberFormat(cellAmount - cellActual)}
              </div>
            ),
            className: 'min-w-157px h-12 text-right py-0',
          },
          // fiscal year
          selectedTime.value !== ActualTimes.FISCAL_CURRENT
            ? {
                content: (
                  <div className='text-right'>
                    {'$' + numberFormat(fiscalAmount)}
                  </div>
                ),
                className: 'min-w-157px h-12 py-0',
              }
            : null,
          selectedTime.value !== ActualTimes.FISCAL_CURRENT
            ? {
                content: (
                  <div className='text-right'>
                    {'$' + numberFormat(fiscalActual)}
                  </div>
                ),
                className: 'min-w-157px h-12 py-0',
              }
            : null,
          selectedTime.value !== ActualTimes.FISCAL_CURRENT
            ? {
                content: (
                  <div
                    className={`text-right ${
                      fiscalAmount - fiscalActual < 0 ? 'text-error-darker' : ''
                    }`}
                  >
                    {'$' + numberFormat(fiscalAmount - fiscalActual)}
                  </div>
                ),
                className: 'min-w-157px h-12 py-0',
              }
            : null,
          // program
          (viewType !== EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT || isProject) && {
            content: '',
            className: 'min-w-157px h-12 py-0',
          },
          {
            content: '',
            className: 'min-w-157px h-12 py-0',
          },
          {
            content: '',
            className: 'min-w-157px h-12 py-0',
          },
          {
            content: '',
            className: 'min-w-157px h-12 py-0',
          },
          {
            content: '',
            className: 'h-12 w-120px py-0 px-1',
          },
        ].filter(Boolean),
      },
      ...(isExpanded
        ? generateLineItemRows(
            parentInfo.lineItems || [],
            layer + 1,
            onEditLineItem,
            programOptions,
            projectOptions,
            vendorOptions,
            teamOptions,
            viewType,
            selectedTime,
            monthsForYearToDate,
            isProject,
            hideDelete,
            handleAddNote,
            handleAddAttachment,
            handleDeleteLineItem
          ).flat()
        : []),
      // Expanded Row for children (if expanded)
      ...(isExpanded && parentInfo.children?.length
        ? generateMultipleReportRows(
            parentInfo.children || [],
            layer + 1,
            expandedRows,
            toggleExpandedRow,
            focusedRows,
            toggleFocusedRow,
            checkedRows,
            onEditLineItem,
            programOptions,
            projectOptions,
            vendorOptions,
            teamOptions,
            viewType,
            fiscalMonthStart,
            monthsForYearToDate,
            selectedTime,
            sumOfAmounts,
            sumOfActuals,
            timeframe,
            focusedCellId,
            setFocusedCellId,
            false,
            hideDelete,
            handleAddNote,
            handleAddAttachment,
            handleDeleteLineItem
          ).flat()
        : []),
    ];
  });
  if (layer === 0 && !isProject) {
    rows.push(
      GenerateTotalRow(
        layer,
        viewType,
        sumOfAmounts,
        sumOfActuals,
        selectedTime
      )
    );
  }

  return rows;
}

export function generateLineItemRows(
  lineItemList: Array<LineItemWithAmounts>,
  layer: number,
  onEditLineItem: (amount: number, lineItemId: string) => void,
  programOptions: Option[],
  projectOptions: Option[],
  vendorOptions: Option[],
  teamOptions: Option[],
  viewType: string,
  selectedTime: Option,
  monthsForYearToDate: number[],
  isProject?: boolean,
  hideDelete?: boolean,
  handleAddNote?: (item: LineItemWithAmounts) => void,
  handleAddAttachment?: (itemId: string) => void,
  handleDeleteLineItem?: (item: LineItemWithAmounts) => void
): Array<any> {
  return lineItemList.map((lineItemInfo) => {
    const allocation = calculateLineItemAllocationCell(
      lineItemInfo,
      selectedTime
    );
    const actual = calculateLineItemActualCell(
      lineItemInfo,
      selectedTime,
      monthsForYearToDate
    );
    const fiscalAmount = lineItemInfo.rawAmounts.reduce(
      (total: number, item: number) => total + (item ?? 0),
      0
    );
    const fiscalActual = lineItemInfo.rawActuals.reduce(
      (total: number, item: number) => total + (item ?? 0),
      0
    );

    return [
      {
        id: `${lineItemInfo.id}-details`,
        className: `group border ${mapRowColorWithLayer(2)} h-12`,
        cells: [
          {
            content: (
              <div
                className='min-w-200px h-full px-4 flex flex-row table-text-truncate items-center overflow-hidden'
                style={{
                  paddingLeft: isProject
                    ? `${layer * 30}px`
                    : `${layer * 10 + 12}px`,
                }}
              >
                {lineItemInfo.description}
              </div>
            ),
            className: stickyCellsStyles(
              classnames('min-w-200px left-0 p-0 h-12'),
              2
            ),
          },
          // q1 allocated
          {
            content: (
              <div className='text-right'>{'$' + numberFormat(allocation)}</div>
            ),
            className: 'min-w-157px h-12',
          },

          // q1 actual
          {
            content:
              lineItemInfo.description !== 'Cost of Labor' ? (
                <div className='min-w-157px h-12 px-1 py-1 flex items-center'>
                  <CurrencyField
                    className='h-full w-full'
                    inputClassName='h-full text-right'
                    value={actual.toFixed(2).toString()}
                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      const { value, selectionStart } = e.currentTarget;
                      const key = e.key;

                      // Allow numbers, one decimal, and navigation keys (Backspace, Arrow keys)
                      if (
                        !/[0-9.]/.test(key) &&
                        !['Backspace', 'ArrowLeft', 'ArrowRight'].includes(key)
                      ) {
                        e.preventDefault();
                      }

                      // Prevent multiple decimal points
                      if (key === '.' && value.includes('.')) {
                        e.preventDefault();
                      }

                      // Ensure only up to two decimal places
                      if (
                        value.includes('.') &&
                        selectionStart !== null &&
                        value.indexOf('.') < selectionStart && // Only enforce if editing decimal part
                        value.split('.')[1]?.length >= 2
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur(); // Triggers onBlur
                      }
                    }}
                    onBlur={(e: any) => {
                      let inputedVale = e.target.value;
                      if (inputedVale) {
                        inputedVale = parseFloat(inputedVale).toFixed(2);
                      }
                      onEditLineItem(inputedVale, lineItemInfo.id);
                    }}
                  />
                </div>
              ) : (
                <div className='min-w-157px h-12 px-3 overflow-hidden py-1 flex items-center justify-end'>
                  {'$' + numberFormat(actual)}
                </div>
              ),
            className: 'h-12 min-w-157px p-0 text-right',
          },

          // q1 variance
          {
            content: (
              <div
                className={`min-w-157px px-4 overflow-hidden py-1 text-right ${
                  allocation - actual < 0 ? 'text-error-darker' : ''
                }`}
              >
                {'$' + numberFormat(allocation - actual)}
              </div>
            ),
            className: 'min-w-157px h-12 text-right p-0',
          },

          // fiscal allocated
          selectedTime.value !== ActualTimes.FISCAL_CURRENT
            ? {
                content: (
                  <div className='min-w-157px px-4 overflow-hidden text-right'>
                    {'$' + numberFormat(fiscalAmount)}
                  </div>
                ),
                className: 'min-w-157px h-12 text-right p-0',
              }
            : null,

          // fiscal actuals
          selectedTime.value !== ActualTimes.FISCAL_CURRENT
            ? {
                content: (
                  <div className='min-w-157px overflow-hidden text-right px-4'>
                    {'$' + numberFormat(fiscalActual)}
                  </div>
                ),
                className: 'min-w-157px h-12 text-right p-0',
              }
            : null,
          // fiscal variance
          selectedTime.value !== ActualTimes.FISCAL_CURRENT
            ? {
                content: (
                  <div
                    className={`min-w-157px px-4 overflow-hidden text-right ${
                      fiscalAmount - fiscalActual < 0 ? 'text-error-darker' : ''
                    }`}
                  >
                    {'$' + numberFormat(fiscalAmount - fiscalActual)}
                  </div>
                ),
                className: 'min-w-157px h-12 text-right p-0',
              }
            : null,

          // team
          viewType !== EDIT_BUDGET_TABS.TEAMS
            ? {
                content: (
                  <div
                    className={`min-w-157px px-4 h-full table-text-truncate flex items-center overflow-hidden`}
                  >
                    {lineItemInfo.learningTeamId
                      ? teamOptions.find(
                          (team) => team.value === lineItemInfo.learningTeamId
                        )?.label
                      : ''}
                  </div>
                ),
                className: 'min-w-157px h-12 p-0',
              }
            : null,
          // program
          viewType !== EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT || isProject
            ? {
                content: (
                  <div
                    className={`min-w-157px px-4 h-full table-text-truncate flex items-center overflow-hidden`}
                  >
                    {lineItemInfo.programId
                      ? programOptions.find(
                          (program) => program.value === lineItemInfo.programId
                        )?.label
                      : ''}
                  </div>
                ),
                className: 'min-w-157px h-12 p-0',
              }
            : null,
          viewType !== EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT
            ? {
                content: (
                  <div
                    className={`min-w-157px px-4 h-full table-text-truncate flex items-center overflow-hidden`}
                  >
                    {lineItemInfo.projectId
                      ? projectOptions.find(
                          (option) => option.value === lineItemInfo.projectId
                        )?.label
                      : ''}
                  </div>
                ),
                className: 'min-w-157px h-12 p-0',
              }
            : null,
          {
            content: (
              <div
                className={`min-w-157px px-4 h-full table-text-truncate flex items-center overflow-hidden`}
              >
                {lineItemInfo.budgetSource}
              </div>
            ),
            className: 'min-w-157px h-12 p-0',
          },
          viewType !== EDIT_BUDGET_TABS.VENDORS
            ? {
                content: (
                  <div
                    className={`min-w-157px px-4 h-full table-text-truncate flex items-center overflow-hidden`}
                  >
                    {lineItemInfo.vendorId
                      ? vendorOptions.find(
                          (vendor) => vendor.value === lineItemInfo.vendorId
                        )?.label
                      : ''}
                  </div>
                ),
                className: 'min-w-157px h-12 p-0',
              }
            : null,

          {
            content: (
              <div className='flex flex-row w-120px justify-center gap-x-4 px-3'>
                <Button
                  variant='tertiary'
                  onClick={() => {
                    if (handleAddNote) {
                      handleAddNote(lineItemInfo);
                    }
                  }}
                  className='w-6 h-6 px-1'
                >
                  <div className='action-icon-wrapper '>
                    <Icon name='chatbox-outline' />
                    {lineItemInfo.note && <div className='active-dot'></div>}
                  </div>
                </Button>
                <Button
                  variant='tertiary'
                  onClick={() => {
                    if (handleAddAttachment) {
                      handleAddAttachment(lineItemInfo.id);
                    }
                  }}
                  className='w-6 h-6 px-1'
                >
                  <div className='action-icon-wrapper'>
                    <Icon name='attach-outline' />
                    {lineItemInfo.hasFile && <div className='active-dot'></div>}
                  </div>
                </Button>
                {handleDeleteLineItem && !hideDelete && (
                  <Button
                    variant='tertiary'
                    onClick={() => {
                      handleDeleteLineItem(lineItemInfo);
                    }}
                    className='w-6 h-6 px-1'
                  >
                    <Icon name='trash-outline' />
                  </Button>
                )}
              </div>
            ),
            className: 'w-120px py-0 px-1 h-12',
          },
        ].filter(Boolean),
      },
    ];
  });
}

export function GenerateTotalRow(
  layer: number,
  viewType: string,
  sumOfAmounts: number,
  sumOfActuals: number,
  selectedTime: Option
): Array<any> {
  const { totalActual, totalAmount } = useBudgetLineItemCalculations();
  return [
    {
      id: `total-report-details`,
      className: `group border ${mapRowColorWithLayer(2)}`,
      cells: [
        {
          content: (
            <div
              className='min-w-200px h-full flex items-center px-4'
              style={{ marginLeft: `${layer * 10}px` }}
            >
              <div>Total</div>
            </div>
          ),
          className: stickyCellsStyles(classnames('min-w-200px p-0 left-0'), 2),
        },
        // q1
        {
          content: (
            <div className='min-w-157px overflow-hidden px-4 text-right'>
              {'$' + numberFormat(sumOfAmounts)}
            </div>
          ),
          className: 'min-w-157px p-0 text-right',
        },
        {
          content: (
            <div className='min-w-157px overflow-hidden px-4 text-right'>
              {'$' + numberFormat(sumOfActuals)}
            </div>
          ),
          className: 'min-w-157px p-0 text-right',
        },
        {
          content: (
            <div
              className={`min-w-157px overflow-hidden px-4 text-right ${
                sumOfAmounts - sumOfActuals < 0 ? 'text-error-darker' : ''
              }`}
            >
              {'$' + numberFormat(sumOfAmounts - sumOfActuals)}
            </div>
          ),
          className: 'min-w-157px p-0 text-right',
        },
        // fiscal year
        selectedTime.value !== ActualTimes.FISCAL_CURRENT
          ? {
              content: (
                <div className='min-w-157px overflow-hidden px-4 text-right'>
                  {'$' + numberFormat(totalAmount)}
                </div>
              ),
              className: 'min-w-157px h-12 p-0',
            }
          : null,
        selectedTime.value !== ActualTimes.FISCAL_CURRENT
          ? {
              content: (
                <div className='min-w-157px overflow-hidden px-4 text-right'>
                  {'$' + numberFormat(totalActual)}
                </div>
              ),
              className: 'min-w-157px h-12 p-0',
            }
          : null,
        selectedTime.value !== ActualTimes.FISCAL_CURRENT
          ? {
              content: (
                <div
                  className={`min-w-157px overflow-hidden px-4 text-right ${
                    totalAmount - totalActual < 0 ? 'text-error-darker' : ''
                  }`}
                >
                  {'$' + numberFormat(totalAmount - totalActual)}
                </div>
              ),
              className: 'min-w-157px h-12 p-0 text-right',
            }
          : null,
        // program
        {
          content: <div className=''></div>,
          className: 'min-w-157px h-12',
        },
        viewType !== EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT
          ? {
              content: <div className=''></div>,
              className: 'min-w-157px h-12',
            }
          : null,
        {
          content: <div className=''></div>,
          className: 'min-w-157px h-12',
        },
        {
          content: <div className=''></div>,
          className: 'min-w-157px h-12',
        },

        {
          content: <div className=''></div>,
          className: 'w-120px h-12',
        },
      ].filter(Boolean),
    },
  ];
}
